import { LocalPinIcon } from '@/components/icons'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { InputAdornment, InputBase } from '@mui/material'
import { FC, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

interface SearchProps {
  onChange?: (coords: { latitude: string; longitude: string }) => void
  value?: string
  className?: string
  colorHolder?: string
  colorText?: string
  sx?: any
  readOnly?: boolean
}

const normalizeCoordinate = (value: string) => {
  return value.replace(/\s+/g, ' ').trim().replace(/,+/g, ',')
}

const validateCoordinates = (value: string) => {
  const normalized = normalizeCoordinate(value)
  const [lat, lon] = normalized.split(',').map((v) => parseFloat(v.trim()))
  const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90
  const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180
  return isValidLat && isValidLon
}

const CoordinateInput: FC<SearchProps> = ({
  value = '',
  onChange,
  colorText,
  colorHolder,
  className,
  sx,
  readOnly,
}) => {
  const themeMode = useSelector(selectTheme)
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const normalizedValue = normalizeCoordinate(inputValue)
    setLocalValue(normalizedValue)
    if (onChange) {
      const parts = normalizedValue.split(',')
      if (parts.length === 2 && validateCoordinates(normalizedValue)) {
        const [latitude, longitude] = parts.map((v) => v.trim())
        onChange({ latitude, longitude })
      } else if (inputValue === '') {
        onChange({ latitude: '', longitude: '' })
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    const normalizedValue = normalizeCoordinate(pastedData)
    setLocalValue(normalizedValue)
    if (onChange) {
      if (pastedData === '') {
        onChange({ latitude: '', longitude: '' })
        return
      }
      const parts = normalizedValue.split(',')
      if (parts.length === 2 && validateCoordinates(normalizedValue)) {
        const [latitude, longitude] = parts.map((v) => v.trim())
        onChange({ latitude, longitude })
      }
    }
  }

  return (
    <InputBase
      className={className}
      value={localValue}
      onChange={handleChange}
      onPaste={handlePaste}
      readOnly={readOnly}
      sx={{
        width: '210px',
        height: '32px',
        backgroundColor: 'background.default',
        border: {
          ...(themeMode === mode_type.dark
            ? { border: '1px solid #283d43' }
            : { border: '1px solid #D7D7D7' }),
        },
        padding: '8px',
        borderRadius: '5px',
        boxShadow: '0px 0px 30px rgba(132, 132, 132, 0.25)',
        '& .MuiInputBase-input::placeholder': {
          color: colorHolder || 'text.main',
        },
        [`& .MuiInputBase-input`]: {
          fontSize: '12px',
          fontWeight: '600',
          lineHeight: '100%',
          color: colorText || 'text.main',
        },
        ...sx,
      }}
      startAdornment={
        <InputAdornment position="start" sx={{ color: '#747474' }}>
          <LocalPinIcon sx={{ color: 'text.main' }} />
        </InputAdornment>
      }
    />
  )
}

export default CoordinateInput
